import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  authFetch,
  bulkEventAction,
  BulkEventAction,
  BulkEventResult,
} from '../auth/api';
import { useAuth } from '../auth/AuthContext';
import { SessionBadge } from './SessionBadge';
import './EventsList.css';

export interface EventSummary {
  id: string;
  title: string;
  trackName: string;
  host: string;
  wsPort: number;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  lapCount: number;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function navigate(path: string): void {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new Event('karting:navigate'));
}

const ACTION_LABELS: Record<BulkEventAction, string> = {
  delete: 'Delete',
  compress: 'Compress',
  decompress: 'Decompress',
};

export const EventsList: React.FC = () => {
  const { isAdmin } = useAuth();
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<BulkEventAction | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const res = await authFetch('/events');
    const data: EventSummary[] = await res.json();
    setEvents(data);
    // Drop anything that no longer exists so the count cannot outlive the rows.
    setSelected((prev) => {
      const ids = new Set(data.map((e) => e.id));
      return new Set(Array.from(prev).filter((id) => ids.has(id)));
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      try {
        await load();
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load events');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allSelected = events.length > 0 && selected.size === events.length;
  const toggleAll = (): void => {
    setSelected(allSelected ? new Set() : new Set(events.map((e) => e.id)));
  };

  const selectedEvents = useMemo(
    () => events.filter((e) => selected.has(e.id)),
    [events, selected],
  );
  const selectedLaps = selectedEvents.reduce((sum, e) => sum + e.lapCount, 0);

  const runAction = async (action: BulkEventAction): Promise<void> => {
    setPending(action);
    setNotice(null);
    setError(null);
    try {
      const results: BulkEventResult[] = await bulkEventAction(
        Array.from(selected),
        action,
      );
      const failed = results.filter((r) => !r.ok);
      const done = results.length - failed.length;

      setNotice(
        failed.length === 0
          ? `${ACTION_LABELS[action]}d ${done} event${done === 1 ? '' : 's'}.`
          : `${ACTION_LABELS[action]}d ${done} of ${results.length}. ` +
              `${failed.length} failed: ${failed[0].error ?? 'unknown error'}`,
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
      setConfirming(false);
    }
  };

  return (
    <div className="App">
      <div className="app-header">
        <h1>Karting Live Timing</h1>
        <SessionBadge />
      </div>
      <div className="app-main">
        <div className="main-content">
          <div className="events-page">
            <div className="events-toolbar">
              <h2 className="events-title">Past events</h2>
              <button
                className="events-link-button"
                onClick={() => navigate('/')}
              >
                ← Back to live
              </button>
            </div>

            {loading && <div className="events-loading">Loading events…</div>}
            {error && <div className="events-error">Error: {error}</div>}
            {notice && <div className="events-notice">{notice}</div>}

            {isAdmin && selected.size > 0 && (
              <div className="events-bulk-bar">
                <span className="events-bulk-count">
                  {selected.size} selected
                  {selectedLaps > 0 && ` · ${selectedLaps.toLocaleString()} laps`}
                </span>
                <button
                  className="events-link-button"
                  disabled={pending !== null}
                  onClick={() => void runAction('compress')}
                >
                  {pending === 'compress' ? 'Compressing…' : 'Compress logs'}
                </button>
                <button
                  className="events-link-button"
                  disabled={pending !== null}
                  onClick={() => void runAction('decompress')}
                >
                  {pending === 'decompress' ? 'Decompressing…' : 'Decompress logs'}
                </button>
                <button
                  className="events-danger-button"
                  disabled={pending !== null}
                  onClick={() => setConfirming(true)}
                >
                  {pending === 'delete' ? 'Deleting…' : 'Delete'}
                </button>
                <button
                  className="events-link-button"
                  disabled={pending !== null}
                  onClick={() => setSelected(new Set())}
                >
                  Clear
                </button>
              </div>
            )}

            {confirming && (
              <div className="events-confirm">
                <p>
                  Delete {selected.size} event
                  {selected.size === 1 ? '' : 's'}, {selectedLaps.toLocaleString()}{' '}
                  lap record{selectedLaps === 1 ? '' : 's'}, and their raw logs from
                  disk? This cannot be undone.
                </p>
                <div className="events-confirm-actions">
                  <button
                    className="events-danger-button"
                    disabled={pending !== null}
                    onClick={() => void runAction('delete')}
                  >
                    Yes, delete
                  </button>
                  <button
                    className="events-link-button"
                    disabled={pending !== null}
                    onClick={() => setConfirming(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && events.length === 0 && (
              <div className="events-empty">No events recorded yet.</div>
            )}

            {!loading && !error && events.length > 0 && (
              <div className="events-table-wrapper">
                <table className="events-table">
                  <thead>
                    <tr>
                      {isAdmin && (
                        <th className="events-select-cell">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            aria-label="Select all events"
                          />
                        </th>
                      )}
                      <th>Title</th>
                      <th>Track</th>
                      <th>Started</th>
                      <th>Duration</th>
                      <th className="num">Laps</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr
                        key={e.id}
                        className="events-row-clickable"
                        onClick={() => navigate(`/events/${e.id}`)}
                      >
                        {isAdmin && (
                          <td
                            className="events-select-cell"
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selected.has(e.id)}
                              onChange={() => toggle(e.id)}
                              aria-label={`Select ${e.title}`}
                            />
                          </td>
                        )}
                        <td className="events-title-cell">{e.title}</td>
                        <td>{e.trackName}</td>
                        <td>{formatDateTime(e.startedAt)}</td>
                        <td>{formatDuration(e.durationMs)}</td>
                        <td className="num">{e.lapCount}</td>
                        <td>
                          {e.endedAt ? (
                            <span className="status-badge status-finished">Finished</span>
                          ) : (
                            <span className="status-badge status-live">Live</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { navigate };
